import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { usePrefersReducedMotion } from "../../hooks/usePrefersReducedMotion";
import "./FbaPricingGuideSection.css";

const PANELS = [
  {
    num: "01",
    nav: "Transparent pricing",
    tag: "Pricing methodology",
    title: "Forecast with confidence",
    body: (
      <>
        Transparent Amazon FBA prep services pricing in the UK helps sellers forecast margins with confidence. Our volume-based pricing structure covers{' '}
        <Link to="/blog/fnsku-labelling-service-uk" className="fba-inline-link">FNSKU labelling</Link>,{' '}
        <Link to="/blog/amazon-poly-bag-requirements-uk" className="fba-inline-link">poly bagging</Link>, packaging, storage, and shipment preparation. We clearly explain every charge, helping businesses budget accurately for peak seasons, promotional demand, high-volume inventory movements, and ongoing Amazon fulfilment requirements.
      </>
    ),
    chips: ["Per-unit FBA rates", "Volume tiers", "No hidden fees"],
  },
  {
    num: "02",
    nav: "Scalable structure",
    tag: "Scalability",
    title: "Pricing that scales with you",
    body: (
      <>
        Our <Link to="/amazon-fba-prep-services" className="fba-inline-link">Amazon FBA prep services UK</Link> pricing is designed to scale efficiently. As shipment volumes increase, batching and streamlined handling reduce per-unit costs. Many sellers begin with essential Amazon prep services before adding branded packaging as sales grow. We also recommend packaging optimisation strategies that lower shipping costs while maintaining product protection for Amazon fulfilment centres, and{' '}
        <Link to="/seller-fulfilled-prime-services" className="fba-inline-link">Seller Fulfilled Prime</Link> orders.
      </>
    ),
    chips: ["Batch efficiency", "Branded packaging", "SFP ready"],
  },
  {
    num: "03",
    nav: "Storage explained",
    tag: "Storage",
    title: "Pay only for what you use",
    body: (
      <>
        Storage pricing is straightforward and scalable. You pay only for the pallet, bin, or shelf space you use, with no long-term contracts that restrict seasonal flexibility. Regular capacity reviews support accurate inventory management, efficient inbound planning, and smooth operations across our{' '}
        <Link to="/amazon-fba-prep-centre-services" className="fba-inline-link">Amazon prep centre in the UK</Link> and fulfilment services.
      </>
    ),
    chips: ["Pallet storage", "Bin locations", "Weekly reviews"],
  },
  {
    num: "04",
    nav: "Courier labels",
    tag: "Couriers & labels",
    title: "Where margin is won or lost",
    body: (
      <>
        Courier selection directly impacts ecommerce profitability. We choose services based on delivery SLA, parcel dimensions, weight, and destination, including Royal Mail Tracked 24/48, DPD Next Day, and international options. For Seller Fulfilled Prime, Amazon Buy Shipping ensures compliant label generation and instant tracking updates. The same operational discipline supports both Amazon and{' '}
        <Link to="/shopify-fulfillment-services" className="fba-inline-link">Shopify fulfilment</Link>.
      </>
    ),
    chips: ["Royal Mail", "DPD Next Day", "Buy Shipping"],
  },
  {
    num: "05",
    nav: "Returns handling",
    tag: "Returns",
    title: "Part of the margin story",
    body: "Returns are more than a cost centre. We inspect and grade returned inventory within 24–48 hours, apply your restock or quarantine rules, and report return reasons. That continuous feedback loop helps reduce repeat issues, improve product performance, and recover margin that would otherwise be lost through preventable returns.",
    chips: ["24–48h grading", "Reason codes", "Restock rules"],
  },
  {
    num: "06",
    nav: "Invoice clarity",
    tag: "Invoicing",
    title: "Transparent Pricing & Billing",
    body: "Every invoice is clear, transparent, and aligned with agreed pricing and operating procedures. As volumes grow or requirements change, rates are reviewed and agreed in advance. We identify efficiencies wherever possible, helping sellers protect margins and scale confidently across Amazon, Shopify, eBay, and TikTok Shop without unexpected costs.",
    chips: ["Itemised billing", "Rate card match", "Multi-channel"],
  },
] as const;

export default function FbaPricingGuideSection() {
  const [active, setActive] = useState(0);
  const prefersReducedMotion = usePrefersReducedMotion();
  const total = PANELS.length;

  const go = (idx: number) => {
    if (idx < 0 || idx >= total) return;
    setActive(idx);
  };

  return (
    <section className="fba-guide-section" aria-label="FBA prep pricing guide">
      <div className="fba-guide-head">
        <p className="fba-guide-eyebrow">Our approach</p>
        <h2 className="fba-guide-title">In this guide</h2>
        <p className="fba-guide-lead">
          Select a topic below to explore detailed guidance on{' '}
          <Link to="/amazon-fba-prep-services" className="fba-inline-link">Amazon FBA prep services UK</Link>.
        </p>
      </div>

      <div className="fba-guide-inner">
        <nav className="fba-guide-sidebar" aria-label="Guide topics">
          <p className="fba-guide-sidebar-label">Topics</p>
          {PANELS.map((p, i) => (
            <button
              key={p.num}
              type="button"
              className={`fba-guide-nav-item${active === i ? " fba-guide-nav-item--active" : ""}`}
              onClick={() => go(i)}
            >
              <span className="fba-guide-nav-num">{p.num}</span>
              <span className="fba-guide-nav-text">{p.nav}</span>
              <span className="fba-guide-nav-arrow" aria-hidden>
                ›
              </span>
            </button>
          ))}
        </nav>

        <div className="fba-guide-content">
          {(() => {
            const p = PANELS[active];
            return (
              <motion.div
                key={p.num}
                role="tabpanel"
                id="fba-guide-panel"
                initial={prefersReducedMotion ? false : { opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.28 }}
              >
                <div className="fba-guide-panel-eyebrow">
                  <span className="fba-guide-panel-num">{p.num}</span>
                  <span className="fba-guide-panel-tag">{p.tag}</span>
                </div>
                <h3 className="fba-guide-panel-title">{p.title}</h3>
                <p className="fba-guide-panel-body">{p.body}</p>
                <div className="fba-guide-chips" role="list">
                  {p.chips.map((c) => (
                    <span key={c} className="fba-guide-chip" role="listitem">
                      {c}
                    </span>
                  ))}
                </div>
              </motion.div>
            );
          })()}

          <div className="fba-guide-nav-buttons">
            <button type="button" className="fba-guide-btn" onClick={() => go(active - 1)} disabled={active === 0}>
              ← Previous
            </button>
            <div className="fba-guide-dots" role="tablist" aria-label="Panel">
              {PANELS.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  className={`fba-guide-dot${active === i ? " fba-guide-dot--active" : ""}`}
                  onClick={() => go(i)}
                  aria-label={`Topic ${i + 1}`}
                  aria-selected={active === i}
                />
              ))}
            </div>
            <button
              type="button"
              className="fba-guide-btn"
              onClick={() => go(active + 1)}
              disabled={active === total - 1}
            >
              Next →
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
